import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Home, ChevronRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageShell from "@/components/PageShell";
import { FAQS } from "@/lib/pages-data";

const SITE = "https://thecoventures.com";

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div
      className={`rounded-2xl border transition ${
        open ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200 bg-white"
      }`}
      data-testid={`faq-item-${i}`}
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        className="w-full text-left flex items-start justify-between gap-4 p-5 md:p-6"
      >
        <span className="font-display text-lg md:text-xl font-semibold text-zinc-950 leading-snug">
          {q}
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 mt-1 text-zinc-500 transition-transform ${
            open ? "rotate-180 text-emerald-600" : ""
          }`}
        />
      </button>
      {open && (
        <div className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 text-zinc-700 text-sm md:text-base leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
      { "@type": "ListItem", position: 2, name: "FAQ", item: SITE + "/faq" },
    ],
  };

  return (
    <PageShell>
      <Seo
        title="Vending Machine FAQ — Price, UPI, PPE, AMC | Thecoventures"
        description="Frequently asked questions about vending machines in India — pricing, UPI cashless payments, PPE machines, AMC and uptime SLA. Answered by Thecoventures."
        canonical={`${SITE}/faq`}
        keywords="vending machine FAQ India, vending machine price, UPI vending machine, PPE vending machine"
        jsonLd={[faqSchema, breadcrumbSchema]}
      />

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-xs text-zinc-500 mb-6"
          data-testid="faq-breadcrumbs"
        >
          <Link to="/" className="hover:text-zinc-900 inline-flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-900">FAQ</span>
        </nav>

        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
          Frequently asked
        </div>
        <h1
          data-testid="faq-h1"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950 leading-[1.02]"
        >
          Vending machine FAQ.
        </h1>
        <p className="mt-5 text-zinc-600 max-w-2xl">
          The answers we hear most — on pricing, cashless payments, PPE machines,
          AMC, uptime and install timelines.
        </p>

        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} i={i} />
          ))}
        </div>

        <div className="mt-14 rounded-3xl border border-zinc-200 bg-zinc-50 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="font-display text-xl md:text-2xl font-semibold text-zinc-950">
              Still have a question?
            </div>
            <div className="text-sm text-zinc-600 mt-1">
              Tell us your site, footfall and product mix — we&apos;ll respond within 24 hours.
            </div>
          </div>
          <Link
            to="/#contact"
            data-testid="faq-cta-quote"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-6 py-3 shadow-[0_10px_24px_rgba(16,185,129,0.3)] transition self-start md:self-auto"
          >
            Get a Free Quote <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
